import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom-v5-compat";
import { Funnel } from "@b2bportal/core-types";
import { StayType } from "@b2bportal/lodging-api";
import {
  HotelNavigationUtility,
  HOTELS_SHOP_PATH,
  HOMES_SHOP_PATH,
  getPackageRoutes,
  useGetCurrentFunnel,
} from "@overrides/utilities";
// Import directly from selector file to avoid circular dependency via @/store index
import { getPackageStateByLodgingId } from "@/store/packages/selectors/hotelAvailabilitySelectors";
import {
  getOfferFunnelEntrySource,
  OFFER_SOURCE_PARAM,
} from "@/tracking/offers/offerFunnelEntrySource";
import { forwardUtmParams } from "@/tracking/utmParams";

export function useLodgingLink(
  id?: string,
  name?: string,
  stayType: StayType = StayType.Hotels
) {
  const [queryParams] = useSearchParams();

  const currFunnel = useGetCurrentFunnel();

  const packageStateByLodgingId = useSelector(getPackageStateByLodgingId);
  const packageToken = packageStateByLodgingId?.[id]?.token?.value;
  const isInPackagesFlow = currFunnel === Funnel.Package;
  // Forward the offer entry source (when this session came from an offer) and
  // the UTM params captured on funnel entry into the shop URL — the shop opens
  // in a new tab that can't read this tab's sessionStorage, so the URL is how
  // it receives the attribution (RBC-2381: UTMs must reach hotel_complete_buy).
  const offerSource = getOfferFunnelEntrySource();

  return useMemo(() => {
    // If the name is not possible to convert to a URL friendly name (for example JP hotels), use "_".
    // The name is not required for the shop to work.
    const urlFriendlyName = HotelNavigationUtility.placeLabelToUrl(name) || "_";

    if (isInPackagesFlow) {
      // Clone queryParams and add packageToken if available
      // (unavailable hotels won't have a token)
      const params = forwardUtmParams(new URLSearchParams(queryParams));
      if (packageToken) {
        params.set("packageToken", packageToken);
      }

      const queryString = params.toString();
      const baseUrl = `${getPackageRoutes().FULL_ROUTES.HOTELS.SHOP}/${urlFriendlyName}/${id}`;
      return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    const shopPath =
      stayType === StayType.Homes ? HOMES_SHOP_PATH : HOTELS_SHOP_PATH;
    const params = forwardUtmParams(new URLSearchParams(queryParams));
    if (offerSource) {
      params.set(OFFER_SOURCE_PARAM, offerSource);
    }
    return `${shopPath}/${urlFriendlyName}/${id}?${params.toString()}`;
  }, [
    id,
    name,
    queryParams,
    stayType,
    isInPackagesFlow,
    packageToken,
    offerSource,
  ]);
}
